import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Sparkles, Star } from 'lucide-react';
import WishlistButton from './WishlistButton';

const MAX_COLOR_DOTS = 5;

const ProductCard = ({
  product,
  onAddToCart,
  onTryOn,
  showAddToCart = true,
  isAddingToCart = false,
  className = '',
}) => {
  const [imgError, setImgError] = useState(false);

  // Unique colors across variants — drives the color-dot hint row below.
  // Mirrors the uniqueColors memo used on the buyer detail page so buyers
  // see a consistent set of colors at-a-glance before clicking in.
  // Declared above the early return to keep hook order stable.
  const uniqueColors = useMemo(() => {
    const m = new Map();
    (product?.variants || []).forEach(v => {
      if (v?.color && !m.has(v.color)) {
        m.set(v.color, v.colorCode || '#cccccc');
      }
    });
    return Array.from(m, ([color, colorCode]) => ({ color, colorCode }));
  }, [product?.variants]);

  if (!product) return null;

  // Compute derived data
  const hasDiscount = product.mrp > product.zammerPrice;
  const discountPercent = hasDiscount
    ? Math.round(((product.mrp - product.zammerPrice) / product.mrp) * 100)
    : 0;
  const savings = hasDiscount ? product.mrp - product.zammerPrice : 0;

  const isNew = product.createdAt &&
    (Date.now() - new Date(product.createdAt).getTime()) < 7 * 24 * 60 * 60 * 1000;

  const totalStock = product.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0);
  const isLowStock = product.inventory?.isLowStock || (totalStock != null && totalStock > 0 && totalStock < 5);
  const isOutOfStock = product.status === 'outOfStock' ||
    product.inventory?.availableQuantity === 0 ||
    (totalStock != null && totalStock === 0);

  const hasSecondImage = product.images && product.images.length > 1 && !imgError;

  // Extract unique sizes from variants
  const sizes = product.variants
    ? [...new Set(product.variants.map(v => v.size).filter(Boolean))]
    : [];

  const rating = product.averageRating || 0;
  const reviewCount = product.numReviews || 0;

  return (
    <div className={`bg-white border border-gray-100 rounded-xl overflow-hidden group hover:shadow-md transition-all duration-300 ${className}`}>
      {/* IMAGE SECTION */}
      <Link to={`/user/product/${product._id}`} className="block">
        <div className="aspect-[2/3] bg-gray-50 relative overflow-hidden">
          {/* Primary Image. object-top, not the default centre: these are
              full-length fashion shots taller than the 2/3 tile, so a centred
              crop takes equal slices off the top and bottom and cuts off the
              model's head. Anchoring to the top trims only the hem. */}
          {product.images && product.images.length > 0 && !imgError ? (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover object-top transition-transform duration-500"
                onError={() => setImgError(true)}
                loading="lazy"
              />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300">
              <ShoppingBag className="w-10 h-10" strokeWidth={1} />
            </div>
          )}

          {/* BADGE STACK - Top Left */}
          <div className="absolute top-2 left-2 flex flex-col gap-0.5 z-10">
            {isNew && (
              <span className="bg-black text-white text-[8px] font-bold px-1.5 py-[2px] rounded uppercase tracking-wider">
                New
              </span>
            )}
            {product.isTrending && (
              <span className="bg-orange-600 text-white text-[8px] font-bold px-1.5 py-[2px] rounded uppercase tracking-wider">
                Trending
              </span>
            )}
            {product.isLimitedEdition && (
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[8px] font-bold px-1.5 py-[2px] rounded uppercase tracking-wider">
                Limited
              </span>
            )}
            {hasDiscount && discountPercent >= 10 && (
              <span className="bg-green-600 text-white text-[8px] font-bold px-1.5 py-[2px] rounded">
                {discountPercent}% OFF
              </span>
            )}
          </div>

          {/* WISHLIST - Top Right */}
          <div className="absolute top-2 right-2 z-20" onClick={(e) => e.preventDefault()}>
            <WishlistButton productId={product._id} size="sm" />
          </div>

          {/* LOW STOCK / OUT OF STOCK */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
              <div className="bg-white px-4 py-2 rounded-lg text-center">
                <p className="text-xs font-bold text-gray-900">Out of Stock</p>
              </div>
            </div>
          )}

          {isLowStock && !isOutOfStock && (
            <div className="absolute bottom-2 left-2 right-2 z-10">
              <span className="bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded animate-pulse inline-block">
                Only {totalStock} left!
              </span>
            </div>
          )}
        </div>
      </Link>

      {/* CONTENT SECTION */}
      <div className="p-3">
        {/* Brand */}
        {product.brand && (
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 line-clamp-1">
            {product.brand}
          </p>
        )}

        {/* Product Name */}
        <Link to={`/user/product/${product._id}`}>
          <h3 className="text-sm font-medium text-gray-900 line-clamp-1 hover:text-gray-600 transition-colors leading-snug mb-1">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        {rating > 0 && (
          <div className="flex items-center gap-1 mb-1.5">
            <div className="flex items-center gap-0.5 bg-green-600 text-white px-1.5 py-0.5 rounded text-[10px] font-bold">
              <span>{rating.toFixed(1)}</span>
              <Star className="w-2.5 h-2.5" fill="currentColor" />
            </div>
            {reviewCount > 0 && (
              <span className="text-[10px] text-gray-400">({reviewCount})</span>
            )}
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-1.5 mb-1.5">
          <span className="text-black font-bold text-base">
            ₹{product.zammerPrice?.toLocaleString('en-IN')}
          </span>
          {hasDiscount && (
            <>
              <span className="text-gray-400 text-xs line-through">
                ₹{product.mrp?.toLocaleString('en-IN')}
              </span>
            </>
          )}
        </div>

        {/* Discount info */}
        {hasDiscount && discountPercent >= 5 && (
          <p className="text-green-600 text-[11px] font-semibold mb-1">
            {discountPercent}% off
          </p>
        )}
        {savings >= 200 && (
          <p className="text-green-700 text-[10px] font-medium bg-green-50 px-1.5 py-0.5 rounded inline-block">
            Best Price ₹{product.zammerPrice?.toLocaleString('en-IN')}
          </p>
        )}

        {/* Color-dot hint row — only render when the product has 2+ colors */}
        {uniqueColors.length > 1 && (
          <div className="flex items-center gap-1 mt-2 mb-1" aria-label={`Available in ${uniqueColors.length} colors`}>
            {uniqueColors.slice(0, MAX_COLOR_DOTS).map(({ color, colorCode }) => (
              <span
                key={color}
                title={color}
                className="w-3 h-3 rounded-full border border-gray-300"
                style={{ backgroundColor: colorCode }}
              />
            ))}
            {uniqueColors.length > MAX_COLOR_DOTS && (
              <span className="text-[10px] text-gray-500 font-medium ml-0.5">
                +{uniqueColors.length - MAX_COLOR_DOTS}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {showAddToCart && (
          <div className={`grid ${onTryOn ? 'grid-cols-2' : 'grid-cols-1'} gap-1.5 mt-1`}>
            <button
              onClick={(e) => {
                e.preventDefault();
                if (!isOutOfStock && onAddToCart) {
                  onAddToCart(product._id, product.name, product);
                }
              }}
              disabled={isAddingToCart || isOutOfStock}
              className={`py-2 rounded-lg text-xs font-semibold transition-colors ${
                isOutOfStock
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : isAddingToCart
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-black hover:bg-gray-800 text-white'
              }`}
            >
              {isOutOfStock ? 'Out of Stock' : isAddingToCart ? 'Adding...' : 'Add to Bag'}
            </button>
            {onTryOn && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onTryOn(product);
                }}
                className="py-2 rounded-lg text-xs font-semibold transition-colors bg-black hover:bg-gray-800 text-white flex items-center justify-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>Try On</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
